import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarDays, UploadCloud } from 'lucide-react';
import { fetchLeaveTypes, applyLeave } from '../../redux/actions/leaveActions';

const ApplyLeave = () => {
  const dispatch = useDispatch();
  const { leaveTypes, loading, error, applyLeaveStatus } = useSelector((state) => state.leave);
console.log("leaveTypes>>>>>>",leaveTypes);

  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    leaveType: '',
    reason: '',
    document: null,
  });

  const [charCount, setCharCount] = useState(0);
  const [leaveDays, setLeaveDays] = useState(0);
  const [documentError, setDocumentError] = useState(''); // ✅ New state for document validation error

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // ✅ Define leave types that require mandatory documents
  const mandatoryDocumentLeaveTypes = [11, 13];

  useEffect(() => {
    dispatch(fetchLeaveTypes());
  }, [dispatch]);

  // Calculate leave days
  useEffect(() => {
    if (form.startDate && form.endDate) {
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);
      const timeDiff = end.getTime() - start.getTime();
      const days = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
      setLeaveDays(days > 0 ? days : 0);
    } else {
      setLeaveDays(0);
    }
  }, [form.startDate, form.endDate]);

  // ✅ Get active leaves from the new data structure
  const getActiveLeaves = () => {
    return leaveTypes?.data?.leaves?.filter(leave => leave.status === 'active') || [];
  };

  // ✅ Check if selected leave type requires mandatory document
  const isDocumentMandatory = () => {
    const activeLeaves = getActiveLeaves();
    const selectedLeave = activeLeaves.find((lt) => lt.leave_type_name === form.leaveType);
    return selectedLeave && mandatoryDocumentLeaveTypes.includes(selectedLeave.leave_type_id);
  };

  // ✅ Clear document error when leave type changes
  useEffect(() => {
    setDocumentError('');
  }, [form.leaveType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'reason') setCharCount(value.length);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setForm((prev) => ({ ...prev, document: file }));
    
    // ✅ Clear document error when file is selected
    if (file && documentError) {
      setDocumentError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Reset document error
    setDocumentError('');

    // Basic required field validation
    if (!form.startDate || !form.endDate || !form.leaveType || !form.reason) {
      alert('Please fill in all required fields.');
      return;
    }

    const activeLeaves = getActiveLeaves();
    const selectedLeave = activeLeaves.find((lt) => lt.leave_type_name === form.leaveType);
    const leaveTypeId = selectedLeave?.leave_type_id;

    // ✅ Check if document is mandatory for this leave type
    if (isDocumentMandatory() && !form.document) {
      setDocumentError('Supporting document is required for this leave type.');
      return;
    }

    console.log("leaveTypeId>>>>>", leaveTypeId);

    const formData = new FormData();
    formData.append('start_date', form.startDate);
    formData.append('end_date', form.endDate);
    formData.append('leave_gov_type', leaveTypeId);
    formData.append('reason', form.reason);
    if (form.document) {
      formData.append('photo', form.document);
    }

    dispatch(applyLeave(formData));
  };

  const openDatePicker = (ref) => {
    if (ref.current) {
      if (typeof ref.current.showPicker === 'function') {
        ref.current.showPicker();
      } else {
        ref.current.focus();
      }
    }
  };

  const activeLeaves = getActiveLeaves();

  return (
    <form onSubmit={handleSubmit} className="text-white py-4">
      {/* Start Date */}
      <div className="mb-4">
        <label className="block mb-1 font-semibold">Start Date *</label>
        <div className="relative">
          <input
            type="date"
            name="startDate"
            ref={startDateRef}
            className="w-full p-3 rounded-lg bg-white text-black"
            value={form.startDate}
            onChange={handleChange}
            required
          />
          <CalendarDays
            onClick={() => openDatePicker(startDateRef)}
            className="absolute top-3 right-3 text-gray-400 cursor-pointer"
          />
        </div>
      </div>

      {/* End Date */}
      <div className="mb-4">
        <label className="block mb-1 font-semibold">End Date *</label>
        <div className="relative">
          <input
            type="date"
            name="endDate"
            ref={endDateRef}
            className="w-full p-3 rounded-lg bg-white text-black"
            value={form.endDate}
            onChange={handleChange}
            required
          />
          <CalendarDays
            onClick={() => openDatePicker(endDateRef)}
            className="absolute top-3 right-3 text-gray-400 cursor-pointer"
          />
        </div>
      </div>

      {/* Leave Days Display Box */}
      {leaveDays > 0 && (
        <div className="bg-[#e6f9f0] rounded-xl p-4 text-center mb-4 border-l-4 border-green-500">
          <p className="text-sm text-green-700 font-medium">Total Leave Days</p>
          <p className="text-lg text-green-800 font-semibold">
            {leaveDays} {leaveDays === 1 ? 'day' : 'days'}
          </p>
        </div>
      )}

      {/* Leave Type */}
      <div className="mb-4">
        <label className="block mb-1 font-semibold">Leave Type *</label>
        <select
          name="leaveType"
          className="w-full p-3 rounded-lg bg-white text-black"
          value={form.leaveType}
          onChange={handleChange}
          required
        >
          <option value="">Select Leave Type</option>
          {loading && <option>Loading...</option>}
          {!loading &&
            activeLeaves.map((leave) => (
              <option key={leave.leave_type_id} value={leave.leave_type_name}>
                {leave.leave_type_name} ({leave.remaining_leaves} remaining out of {leave?.total_leaves})
              </option>
            ))}
        </select>
        {error && <p className="text-red-400 mt-1">{error}</p>}
      </div>

      {/* Reason */}
      <div className="mb-4">
        <label className="block mb-1 font-semibold">Reason for Leave *</label>
        <textarea
          name="reason"
          rows="3"
          maxLength={500}
          placeholder="Reason"
          className="w-full p-3 rounded-lg bg-white text-black"
          value={form.reason}
          onChange={handleChange}
          required
        />
        <div className="text-right text-sm mt-1 text-gray-400">{charCount}/500</div>
      </div>

      {/* Upload */}
      <div className="mb-4">
        <label className="block mb-2 font-semibold">
          Supporting Document {isDocumentMandatory() && <span className="text-red-400">*</span>}
          {!isDocumentMandatory() && <span className="text-gray-400">(Optional)</span>}
        </label>
        
        {/* ✅ Show mandatory document notice */}
        {isDocumentMandatory() && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-3 rounded">
            <p className="text-sm font-medium">
              📋 Document upload is required for this leave type
            </p>
          </div>
        )}

        <input
          type="file"
          id="uploadDoc"
          onChange={handleFileChange}
          accept=".jpg,.jpeg,.png,.pdf"
          className="hidden"
        />
        <label htmlFor="uploadDoc">
          <div className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg cursor-pointer ${
            isDocumentMandatory() 
              ? 'bg-orange-100 text-orange-600 border-2 border-orange-300' 
              : 'bg-white text-sky-600'
          }`}>
            <UploadCloud size={18} /> 
            {isDocumentMandatory() ? 'Upload Required Document' : 'Upload File'}
          </div>
        </label>
        
        {form.document && (
          <p className="text-sm text-green-400 mt-2">Selected: {form.document.name}</p>
        )}
        
        {/* ✅ Show document error */}
        {documentError && (
          <p className="text-red-400 mt-2 text-sm font-medium">{documentError}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="w-full bg-green-500 py-3 rounded-lg font-bold text-white mb-20"
      >
        Apply Leave
      </button>

      {/* Feedback */}
      {applyLeaveStatus === 'success' && (
        <p className="text-green-400 mt-3 font-semibold text-center">Leave applied successfully!</p>
      )}
      {error && (
        <p className="text-red-400 mt-3 font-semibold text-center">{error}</p>
      )}
    </form>
  );
};

export default ApplyLeave;